import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Crown, Medal, Award, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TopReaders } from "@/components/TopReaders";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HonorRoll() {
  const [entries, setEntries] = useState([]);
  const [classes, setClasses] = useState([]);
  const [period, setPeriod] = useState("Trimestre 1");
  const [classFilter, setClassFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { period };
    if (classFilter !== "all") params.class_name = classFilter;
    axios.get(`${API}/honor-roll`, { params })
      .then(({ data }) => {
        setEntries(data);
        const uniqueClasses = [...new Set(data.map(e => e.class_name))].sort();
        if (classes.length === 0) setClasses(uniqueClasses);
      })
      .finally(() => setLoading(false));
  }, [period, classFilter]);

  // Group by class
  const grouped = entries.reduce((acc, e) => {
    if (!acc[e.class_name]) acc[e.class_name] = [];
    acc[e.class_name].push(e);
    return acc;
  }, {});

  return (
    <div className="bg-[#F8FAFC] min-h-screen pb-20" data-testid="page-honor-roll-public">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#FBBF24] via-[#EA580C] to-[#1E3A8A] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-dot-pattern" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <Trophy className="h-14 w-14 mx-auto mb-5 text-white drop-shadow-lg" />
          <div className="chip bg-white/15 border-white/20 text-[#FBBF24] mb-5 inline-flex"><Crown className="h-3 w-3" /><span>Tableau d&apos;honneur</span></div>
          <h1 className="mazid-headline text-5xl sm:text-6xl mb-4 tracking-[-0.03em]">Les visages de l&apos;excellence</h1>
          <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
            Félicitations aux élèves qui se sont distingués par leurs résultats et leur engagement.
          </p>
        </div>
      </section>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex gap-3 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-56" data-testid="hr-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Trimestre 1">Trimestre 1</SelectItem>
              <SelectItem value="Trimestre 2">Trimestre 2</SelectItem>
              <SelectItem value="Trimestre 3">Trimestre 3</SelectItem>
              <SelectItem value="Année complète">Année complète</SelectItem>
            </SelectContent>
          </Select>
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger className="w-56" data-testid="hr-class-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les classes</SelectItem>
              {classes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading && <div className="text-center py-16"><Loader2 className="h-7 w-7 animate-spin mx-auto text-slate-400" /></div>}

        {!loading && entries.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <Trophy className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <h3 className="font-heading font-bold text-xl text-[#1E3A8A] mb-2">Tableau d&apos;honneur en préparation</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Les résultats seront publiés ici par l&apos;administration dès la fin du trimestre. Revenez prochainement !
            </p>
          </div>
        )}

        {/* Grouped by class */}
        {Object.entries(grouped).map(([className, items]) => (
          <div key={className} className="mt-10" data-testid={`hr-class-block-${className}`}>
            <div className="flex items-center gap-3 mb-5">
              <h2 className="font-heading font-extrabold text-2xl text-[#1E3A8A]">{className}</h2>
              <div className="flex-1 h-px bg-gradient-to-r from-[#FBBF24] to-transparent" />
              <span className="text-xs text-slate-500">{items.length} élève(s) à l&apos;honneur</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {items.map(e => {
                const isTop3 = e.rank <= 3;
                const podiumColor = e.rank === 1 ? "#FBBF24" : e.rank === 2 ? "#94A3B8" : "#CD7F32";
                return (
                  <div key={e.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1" data-testid={`hr-entry-${e.id}`}>
                    <div className="aspect-square relative bg-gradient-to-br from-[#1E3A8A] to-[#166534]">
                      {e.photo_url ? (
                        <img src={e.photo_url} alt={e.student_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white font-heading font-extrabold text-5xl opacity-50">
                          {e.student_name.split(" ").map(n => n[0]).slice(0, 2).join("")}
                        </div>
                      )}
                      {/* Rank badge */}
                      <div
                        className="absolute top-2 left-2 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-lg ring-2 ring-white"
                        style={{ backgroundColor: isTop3 ? podiumColor : "#1E3A8A" }}
                      >
                        {e.rank === 1 ? <Crown className="h-4 w-4" /> : e.rank === 2 ? <Medal className="h-4 w-4" /> : e.rank === 3 ? <Award className="h-4 w-4" /> : <span className="text-sm">#{e.rank}</span>}
                      </div>
                      {/* Average */}
                      <div className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-white/95 text-[#166534] font-heading font-extrabold text-sm shadow">
                        {e.average.toFixed(2)}/20
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="font-heading font-bold text-sm text-[#0F172A] truncate" title={e.student_name}>{e.student_name}</div>
                      {e.comment && <div className="text-[11px] text-slate-500 mt-1 line-clamp-2 italic">&quot;{e.comment}&quot;</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Meilleurs lecteurs — badges bibliothèque */}
        <TopReaders />
      </div>
    </div>
  );
}
